import { FFmpeg } from '@ffmpeg/ffmpeg';
import { useNavigate } from 'react-router-dom';
import { Dropzone, FileMosaic } from '@dropzone-ui/react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

// import { IEvaluacion } from '../constants';
import { sendVideoUsuario } from '../../../Services/Form';
import { IUserFromStorage } from '../../../Interfaces/IUser';
import { Tr4inerContainedButton } from '../../../Utils/Button';
import { convertMovToMp4, DropzoneFile, getVideoDuration, loadFFmpeg } from '../../../Pages/UploadVideo/constants';
import LoadingAllScreen from '../../LoadingAllScreen/LoadingAllScreen';

// import CloseIcon from '@mui/icons-material/Close';


// interface IProps {
//   setSection: Dispatch<SetStateAction<number>>;
//   dataEvaluacion: IEvaluacion;
//   handleChange(type: string, value: string | number): void;
// }

const UploadVideoSection = () => {

  const navigate = useNavigate();
  const ffmpegRef = useRef(new FFmpeg());
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [videoFile, setVideoFile] = useState<any>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const user: IUserFromStorage = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    loadFFmpeg(ffmpegRef, setFfmpegLoaded);
  }, []);

  const updateVideoFile = async (incomingFiles: DropzoneFile[]): Promise<void> => {

    if (incomingFiles.length === 0) {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      setVideoUrl('');
      setVideoFile(null);
      return;
    }

    const e = incomingFiles[0];
    
    if (isConverting) {
      toast.error('Ya hay un video en proceso. Espera a que termine.');
      return;
    }

    if (videoFile) {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      toast.success('Video anterior reemplazado');
    }

    if (incomingFiles.length > 1) {
      toast.error('Solo puedes subir 1 video. Se tomó el primero.');
    }
    
    if (!e || e.valid !== true) {
      setVideoUrl('');
      setVideoFile(null);
      return;
    }

    let file = e.file;

    if (file.type !== 'video/mp4' && file.type !== 'video/quicktime') {
      toast.error('El archivo debe ser formato MP4 o MOV.');
      setVideoUrl('');
      setVideoFile(null);
      return;
    }

    if (file.size >= 100 * 1024 * 1024) {
      toast.error('El video es demasiado grande (máx 100 MB).');
      setVideoUrl('');
      setVideoFile(null);
      return;
    }

    try {
      const duration = await getVideoDuration(file);
      
      if (duration > 30) {
        toast.error(`El video excede los 30 segundos (duración: ${Math.round(duration)}s).`);
        setVideoUrl('');
        setVideoFile(null);
        return;
      }
    } catch (error) {
      toast.error('Error al leer el video.');
      setVideoUrl('');
      setVideoFile(null);
      return;
    }

    if (file.type === 'video/quicktime') {
      const sizeInMB = (file.size / (1024 * 1024)).toFixed(1);
      const toastId = toast.loading(`Convirtiendo video (${sizeInMB} MB)... 0%`);
      
      try {
        setIsConverting(true);
        
        const ffmpeg = ffmpegRef.current;
        
        const progressHandler = ({ progress }: { progress: number }): void => {
          const percent = Math.round(progress * 100);

          toast.loading(`Convirtiendo video a MP4... ${percent}%`, { id: toastId });
        };
        
        ffmpeg.on('progress', progressHandler);
        
        file = await convertMovToMp4(file, ffmpegLoaded, ffmpegRef);
        
        ffmpeg.off('progress', progressHandler);
        
        toast.success(`¡Video convertido exitosamente!`, { id: toastId });
        
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al convertir el video', { id: toastId });
        setIsConverting(false);
        setVideoUrl('');
        setVideoFile(null);
        return;
      } finally {
        setIsConverting(false);
      }
    }

    try {
      const newVideoUrl = URL.createObjectURL(file);
      const finalDuration = await getVideoDuration(file);
      
      const updatedFile: DropzoneFile = {
        ...e,
        file,
        videoUrl: newVideoUrl,
        duration: finalDuration,
      };
      
      setVideoUrl(newVideoUrl);
      setVideoFile(updatedFile);

    } catch (error) {
      toast.error('Error al procesar el video.');
      setVideoUrl('');
      setVideoFile(null);
    }
  };

  const uploadVideoFunction = () => {
    if (!videoFile) {
      toast.error('No hay video para subir');
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append('Video', videoFile.file, videoFile.file.name);
    formData.append('UsuarioTestAsesoriaID', user.usuarioTestAsesoriaID.toString());

    console.log('Tamaño:', (videoFile.file.size / (1024 * 1024)).toFixed(2), 'MB');

    sendVideoUsuario(
      formData
    )
      .then(() => {
        toast.success('Video enviado correctamente.');
        setVideoFile(null);
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
          setVideoUrl('');
        }
        navigate('/despuesTest');
      })
      .catch((error) => {
        toast.error('Hubo un error al subir video, por favor intente nuevamente más tarde.');
        console.error('Error al subir video:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <>
      {(isLoading || !ffmpegLoaded)&& (
        <div className="absolute bg-slate-500/30 top-0 w-full min-h-[100dvh]">
          <LoadingAllScreen />
        </div>
      )}
      <section className="flex flex-col gap-1 font-bold justify-center items-center mt-2">

        <b className="text-orangeTr4iner text-xl text-center">Sube tu video</b>

        <div className="flex flex-col items-center justify-center w-max m-auto text-sm md:text-base">
          {' '}
          <div className="mt-3 flex justify-start items-center w-full font-semibold">
            <span className="h4 font-bold text-left">1&nbsp;</span>
            Sosteniendo el mismo cartel
          </div>
          <div className="flex justify-start items-center w-full font-semibold">
            <span className="h4 font-bold">2&nbsp;</span>
            Grabándote de cuerpo completo (de cabeza a pies)
          </div>
          <div className="flex justify-start items-center w-full font-semibold">
            <span className="h4 font-bold ">3&nbsp;</span>
            Frente a una pared clara y con buena iluminación
          </div>
          <div className="justify-start items-left w-full flex flex-col font-semibold">
            <div className='w-full flex items-center'>
              <span className="h4 font-bold">4&nbsp;</span>
              Debes girar lentamente mostrando 4 posiciones:
            </div>
            <ul className="list-disc ml-4 items-start flex flex-col justify-start font-semibold">
              <li>De frente</li>
              <li>De perfil derecho</li>
              <li>De perfil izquierdo</li>
              <li>De espalda</li>
            </ul>
          </div>
           
          <div className="flex justify-start items-center w-full font-semibold">
            <span className="h4 font-bold ">5&nbsp;</span>
            El video debe durar máximo 30 segundos
          </div>
        </div>
        <div className="m-1 mt-2 mb-3 font-bold">
          <strong>Nota:</strong>  Al terminar de convertir el video, podrás enviarlo.
        </div>
      </section>

      <div 
        className="lg:w-[70%] w-[80%] flex justify-center ml-auto mr-auto my-1">
        <Dropzone
          onChange={updateVideoFile}
          accept="video/mp4,video/quicktime"
          maxFiles={1}
          label="Arrastra tu video aquí (MP4 o MOV, máx 30s)"
          minHeight="195px"
          value={videoFile ? [videoFile] : []}
          disabled={(isConverting || isLoading || !ffmpegLoaded)}
        >
          {videoFile && (
            <FileMosaic {...videoFile} preview />
          )}
        </Dropzone>
      </div>
      <div className="w-full flex justify-center">
        <Tr4inerContainedButton
          variant="contained"
          style={{
            fontWeight: 'bold',
            textTransform: 'inherit',
            margin: '1rem 0',
          }}
          disabled={videoFile === null || isConverting && videoFile}
          onClick={uploadVideoFunction}
          >
          Enviar
        </Tr4inerContainedButton>
      </div>
    </>
  );
}

export default UploadVideoSection